import React, { useMemo, useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import AddPurchaseChallanForm from './AddPurchaseChallanForm';
import API_BASE_URL from '../config/apiConfig.js';
import fetchApi from '../utils/api.js';
import { usePermissionContext } from '../contexts/PermissionContext.js';
import AirDatePicker from '../components/ui/AirDatePicker';
import SearchBar from '../components/ui/SearchBar';
import PageSkeleton from '../components/ui/PageSkeleton';
import DataTable from '../components/ui/DataTable';


function formatDateDDMMYYYY(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = String(d.getFullYear());
  return `${dd}-${mm}-${yyyy}`;
}

function formatDateTimeDDMMYYYY(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = String(d.getFullYear());
  const hh = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  const ss = String(d.getSeconds()).padStart(2, '0');
  return `${dd}-${mm}-${yyyy}-${hh}:${min}:${ss}`;
}

const getInvoiceDateTime = (r) => {
  if (r.created_at) return formatDateTimeDDMMYYYY(r.created_at);
  if (r.createdAt) return formatDateTimeDDMMYYYY(r.createdAt);
  return formatDateTimeDDMMYYYY(r.invoiceDate || r.challan_date);
};

function dateToComparable(value) {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}



function PurchaseChallanList({ onPreview, onAddInvoice, onEditInvoice, onConvert, onNavigateToProfile, isReport = false }) {
  const { hasPermission } = usePermissionContext();
  const [rows, setRows] = useState([]);
  const token = sessionStorage.getItem('token');
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [history, setHistory] = useState([]);

  const [draftSeller, setDraftSeller] = useState('');
  const [appliedSeller, setAppliedSeller] = useState('');

  const [draftStartDate, setDraftStartDate] = useState('');
  const [draftEndDate, setDraftEndDate] = useState('');
  const [appliedStartDate, setAppliedStartDate] = useState('');
  const [appliedEndDate, setAppliedEndDate] = useState('');

  const [exportType, setExportType] = useState('');
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [exportColumns, setExportColumns] = useState({
    companyName: true,
    buyerTradeName: true,
    companyState: true,
    invoiceNo: true,
    invoiceDate: true,
    totalAmount: true
  });

  const columnLabels = {
    companyName: 'Seller Company Name',
    buyerTradeName: 'Company Name',
    companyState: 'Place of Supply',
    invoiceNo: 'Challan No',
    invoiceDate: 'Challan Date',
    totalAmount: 'Total Amount'
  };

  const toggleExportColumn = (columnKey) => {
    setExportColumns(prev => ({
      ...prev,
      [columnKey]: !prev[columnKey]
    }));
  };

  const user = JSON.parse(sessionStorage.getItem('user'));
  const isSuperAdmin = user?.role === 'SUPER_ADMIN';

  const totalCols = 7 + (isSuperAdmin ? 1 : 0) + (hasPermission('Purchase Challan', 'view') ? 1 : 0) + ((!isReport && (hasPermission('Purchase Challan', 'update') || hasPermission('Purchase Challan', 'delete'))) ? 1 : 0);
  const [search, setSearch] = useState('');
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(() => {
    const savedPage = sessionStorage.getItem('lastPage_PurchaseChallanList');
    return savedPage ? parseInt(savedPage) : 1;
  });
  const [totalEntries, setTotalEntries] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' });
  const [sellers, setSellers] = useState([]);

  useEffect(() => {
    fetchSellers();
  }, []);

  const fetchSellers = async () => {
    try {
      const result = await fetchApi('/seller?limit=1000');
      const sellerList = result.sellers || (Array.isArray(result) ? result : []);
      setSellers(sellerList);
    } catch (error) {
      console.error("Fetch Sellers Error:", error);
    }
  };

  const fetchPurchaseInvoices = async (searchQuery = '', currentPage = 1, limit = 10, filters = {}) => {
    try {
      if (!searchQuery) setLoading(true);
      const queryParams = new URLSearchParams();
      if (searchQuery) queryParams.append('search', searchQuery);
      queryParams.append('page', currentPage);
      queryParams.append('limit', limit);
      if (filters.seller) queryParams.append('seller', filters.seller);
      if (filters.startDate) queryParams.append('startDate', filters.startDate);
      if (filters.endDate) queryParams.append('endDate', filters.endDate);
      queryParams.append('status', 'Pending');
      const result = await fetchApi(`/purchase-challan?${queryParams.toString()}`);
      if (result.purchaseChallans && Array.isArray(result.purchaseChallans)) {
        setRows(result.purchaseChallans);
        setTotalEntries(result.total || 0);
        setTotalPages(result.pages || 1);
      } else if (Array.isArray(result)) {
        setRows(result);
        setTotalEntries(result.length);
        setTotalPages(Math.ceil(result.length / limit));
      }
    } catch (error) {
      console.error("Fetch Error:", error);
      if (error.status !== 403) {
        toast.error("Server error");
      }
    } finally {
      setLoading(false);
    }
  };

  const [selectedIdx, setSelectedIdx] = useState(0);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchPurchaseInvoices(search, page, pageSize, {
        seller: appliedSeller,
        startDate: appliedStartDate,
        endDate: appliedEndDate
      });
    }, 200);

    fetchHistory();
    return () => clearTimeout(delayDebounceFn);
  }, [search, page, pageSize, appliedSeller, appliedStartDate, appliedEndDate, isReport]);

  const fetchHistory = async () => {
    try {
      const data = await fetchApi('/purchase-challan?limit=20&status=Converted');
      setHistory(data.purchaseChallans || []);
    } catch (err) {
      console.error("History fetch error:", err);
    }
  };

  const filteredRows = useMemo(() => {
    return rows;
  }, [rows]);

  const sortedRows = useMemo(() => {
    let sortableItems = [...filteredRows];
    if (sortConfig.key !== null) {
      sortableItems.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        // Handle special cases
        if (sortConfig.key === 'totalAmount') {
          aValue = parseFloat(a.totalAmount || 0);
          bValue = parseFloat(b.totalAmount || 0);
        } else if (sortConfig.key === 'invoiceDate') {
          aValue = new Date(aValue).getTime();
          bValue = new Date(bValue).getTime();
        } else {
          aValue = aValue ? aValue.toString().toLowerCase() : '';
          bValue = bValue ? bValue.toString().toLowerCase() : '';
        }

        if (aValue < bValue) {
          return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableItems;
  }, [filteredRows, sortConfig]);

  const pagedRows = sortedRows;
  const total = totalEntries;

  // Keyboard navigation: Up, Down, Enter
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (pagedRows.length === 0) return;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIdx((prev) => (prev + 1) % pagedRows.length);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIdx((prev) => (prev - 1 + pagedRows.length) % pagedRows.length);
      } else if (e.key === 'Enter') {
        e.preventDefault();
        const selectedItem = pagedRows[selectedIdx];
        if (selectedItem) {
          handleSellerClick(selectedItem);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [pagedRows, selectedIdx]);

  const requestSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const showingFrom = totalEntries === 0 ? 0 : (page - 1) * pageSize + 1;
  const showingTo = Math.min(totalEntries, (page - 1) * pageSize + rows.length);

  const pages = useMemo(() => {
    const out = [];
    const maxButtons = 5;
    let start = Math.max(1, page - Math.floor(maxButtons / 2));
    let end = Math.min(totalPages, start + maxButtons - 1);
    start = Math.max(1, end - maxButtons + 1);
    for (let i = start; i <= end; i += 1) out.push(i);
    return out;
  }, [page, totalPages]);

  const onSubmitFilters = (e) => {
    e.preventDefault();

    let finalEndDate = draftEndDate;
    if (draftStartDate && !draftEndDate) {
      finalEndDate = new Date().toISOString().split('T')[0];
      setDraftEndDate(finalEndDate);
    }

    if (!draftStartDate && draftEndDate) {
      toast.error("Please enter Start Date");
      return;
    }

    setAppliedSeller(draftSeller);
    setAppliedStartDate(draftStartDate);
    setAppliedEndDate(finalEndDate);
    setPage(1);
  };

  const onResetFilters = () => {
    setDraftSeller('');
    setAppliedSeller('');
    setDraftStartDate('');
    setDraftEndDate('');
    setAppliedStartDate('');
    setAppliedEndDate('');
    setSearch('');
    setPage(1);
  };

  const toggleActive = async (id) => {
    try {
      const row = rows.find(r => r._id === id);
      await fetchApi(`/purchase-challan/${id}`, {
        method: 'PUT',
        body: JSON.stringify({ active: !row.active })
      });
      setRows((prev) => prev.map((r) => (r._id === id ? { ...r, active: !r.active } : r)));
      toast.success(`Challan ${!row.active ? 'Activated' : 'Deactivated'}`);
    } catch (error) {
      if (error.status !== 403) {
        toast.error("Failed to update status");
      }
    }
  };

  const handleExcelDownload = async () => {
    try {
      setLoading(true);
      const queryParams = new URLSearchParams();
      if (search) queryParams.append('search', search);
      if (appliedSeller) queryParams.append('seller', appliedSeller);
      if (appliedStartDate) queryParams.append('startDate', appliedStartDate);
      if (appliedEndDate) queryParams.append('endDate', appliedEndDate);
      queryParams.append('limit', '0');
      queryParams.append('status', 'Pending');

      const result = await fetchApi(`/purchase-challan?${queryParams.toString()}`);
      let invoicesToExport = [];
      if (result.purchaseChallans && Array.isArray(result.purchaseChallans)) {
        invoicesToExport = result.purchaseChallans;
      } else if (Array.isArray(result)) {
        invoicesToExport = result;
      }

      const worksheetData = invoicesToExport.map((r, index) => {
        const row = { 'No.': index + 1 };
        if (exportColumns.companyName) row['Seller Company Name'] = r.companyName;
        if (isSuperAdmin && exportColumns.buyerTradeName) row['Company Name'] = r.buyerTradeName || '';
        if (exportColumns.companyState) row['Place of Supply'] = r.companyState;
        if (exportColumns.invoiceNo) row['Challan No'] = r.invoiceNo;
        if (exportColumns.invoiceDate) row['Challan Date'] = formatDateDDMMYYYY(r.invoiceDate);
        if (exportColumns.totalAmount) row['Total Amount'] = r.totalAmount;
        return row;
      });

      const worksheet = XLSX.utils.json_to_sheet(worksheetData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'PurchaseChallans');
      XLSX.writeFile(workbook, 'PurchaseChallanList.xlsx');
    } catch (error) {
      console.error("Excel Download Error:", error);
      toast.error("Failed to download Excel");
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = async () => {
    try {
      setLoading(true);
      const queryParams = new URLSearchParams();
      if (search) queryParams.append('search', search);
      if (appliedSeller) queryParams.append('seller', appliedSeller);
      if (appliedStartDate) queryParams.append('startDate', appliedStartDate);
      if (appliedEndDate) queryParams.append('endDate', appliedEndDate);
      queryParams.append('limit', '0');
      queryParams.append('status', 'Pending');

      const result = await fetchApi(`/purchase-challan?${queryParams.toString()}`);
      let invoicesToPrint = [];
      if (result.purchaseChallans && Array.isArray(result.purchaseChallans)) {
        invoicesToPrint = result.purchaseChallans;
      } else if (Array.isArray(result)) {
        invoicesToPrint = result;
      }

      const printWindow = window.open('', '', 'height=600,width=800');
      printWindow.document.write(`<html><head><title>${isReport ? 'Purchase Challan Report' : 'Purchase Challan List'}</title>`);
      printWindow.document.write('<style>');
      printWindow.document.write('body { font-family: Arial, sans-serif; margin: 20px; }');
      printWindow.document.write('h1 { text-align: center; color: #0b3a54; }');
      printWindow.document.write('table { width: 100%; border-collapse: collapse; margin-top: 20px; font-size: 10px; }');
      printWindow.document.write('th, td { border: 1px solid #ddd; padding: 5px; text-align: left; }');
      printWindow.document.write('th { background-color: #dff5ea; color: #0b3a54; font-weight: 600; }');
      printWindow.document.write('</style></head><body>');
      printWindow.document.write(`<h1>${isReport ? 'Purchase Challan Report' : 'Purchase Challan List'}</h1>`);
      printWindow.document.write('<table><thead><tr>');
      printWindow.document.write('<th>No.</th>');
      if (exportColumns.companyName) printWindow.document.write('<th>Seller Company Name</th>');
      if (isSuperAdmin && exportColumns.buyerTradeName) printWindow.document.write('<th>Company Name</th>');
      if (exportColumns.companyState) printWindow.document.write('<th>Place of Supply</th>');
      if (exportColumns.invoiceNo) printWindow.document.write('<th>Challan No</th>');
      if (exportColumns.invoiceDate) printWindow.document.write('<th>Challan Date</th>');
      if (exportColumns.totalAmount) printWindow.document.write('<th>Total Amount</th>');
      printWindow.document.write('</tr></thead><tbody>');

      invoicesToPrint.forEach((r, index) => {
        printWindow.document.write('<tr>');
        printWindow.document.write(`<td>${index + 1}</td>`);
        if (exportColumns.companyName) printWindow.document.write(`<td>${r.companyName || ''}</td>`);
        if (isSuperAdmin && exportColumns.buyerTradeName) {
          printWindow.document.write(`<td>${r.buyerTradeName || ''}</td>`);
        }
        if (exportColumns.companyState) printWindow.document.write(`<td>${r.companyState || ''}</td>`);
        if (exportColumns.invoiceNo) printWindow.document.write(`<td>${r.invoiceNo || ''}</td>`);
        if (exportColumns.invoiceDate) printWindow.document.write(`<td>${formatDateDDMMYYYY(r.invoiceDate)}</td>`);
        if (exportColumns.totalAmount) printWindow.document.write(`<td>${r.totalAmount || 0}</td>`);
        printWindow.document.write('</tr>');
      });
      printWindow.document.write('</tbody></table></body></html>');
      printWindow.document.close();
      printWindow.print();
    } catch (error) {
      console.error("Print Error:", error);
      toast.error("Failed to print");
    } finally {
      setLoading(false);
    }
  };

  const deleteRow = (id) => {
    Swal.fire({
      title: 'Are you sure?',
      text: "Do you want to delete this data?",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!',
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await fetchApi(`/purchase-challan/${id}`, { method: 'DELETE' });
          setRows((prev) => prev.filter((r) => r._id !== id));
          Swal.fire('Deleted!', 'Purchase challan has been deleted.', 'success');
        } catch (error) {
          if (error.status !== 403) {
            Swal.fire('Error!', 'Server error.', 'error');
          }
        }
      }
    });
  };

  const handleSellerClick = async (invoice) => {
    try {
      if (!invoice.purchaseCompanyId) {
        toast.error("Seller ID not found in invoice");
        return;
      }

      const sellerData = await fetchApi(`/seller/${invoice.purchaseCompanyId}`);
      if (sellerData) {
        if (onNavigateToProfile) {
          onNavigateToProfile(sellerData);
        }
      } else {
        toast.error("Failed to fetch seller details");
      }
    } catch (error) {
      console.error("Fetch Error:", error);
      if (error.status !== 403) {
        toast.error("Server error");
      }
    }
  };

  const startEdit = (row) => {
    if (onEditInvoice) {
      onEditInvoice(row);
    } else {
      setEditingRow(row);
      setShowAddForm(true);
    }
  };

  const closeForm = () => {
    setShowAddForm(false);
    setEditingRow(null);
  };

  if (loading) {
    return <PageSkeleton />;
  }

  const reportColumns = [
    { key: 'invoiceDate', label: 'PURCHASE CHALLAN DATE', sortable: true, render: (r) => getInvoiceDateTime(r) },
    { key: 'paymentType', label: 'C/D', render: () => 'Debit' },
    { key: 'invoiceNo', label: 'PURCHASE CHALLAN NO', sortable: true },
    { 
      key: 'companyName', 
      label: 'PARTY NAME', 
      sortable: true, 
      render: (r, idx) => (
        <div
          className={`party-name-box ${idx === selectedIdx ? 'party-name-box--active' : ''}`}
          onClick={() => {
            setSelectedIdx(idx);
            handleSellerClick(r);
          }}
        >
          {r.companyName}
        </div>
      )
    },
    { key: 'companyCity', label: 'CITY', sortable: true, render: (r) => r.companyCity || r.company_city || '' },
    { key: 'totalAmount', label: 'TOTAL AMOUNT', sortable: true, render: (r) => `${parseFloat(r.totalAmount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/-` },
    { key: 'buyerTradeName', label: 'BILL BY', sortable: true, render: (r) => r.buyerTradeName || r.buyer_trade_name || '' }
  ];

  if (hasPermission('Purchase Challan', 'view')) {
    reportColumns.push({
      key: 'preview',
      label: 'PREVIEW',
      render: (r) => (
        <button type="button" className="list-page-btn list-page-btn-excel" style={{ padding: '6px 12px', fontSize: '12px', margin: 0, border: 'none', backgroundColor: '#0b3a54', color: 'white', borderRadius: '4px' }} onClick={() => onPreview && onPreview(r)}>Preview</button>
      )
    });
  }

  const standardColumns = [
    {
      key: 'companyName',
      label: 'SELLER COMPANY NAME',
      sortable: true,
      render: (r, idx) => (
        <div
          className={`party-name-box ${idx === selectedIdx ? 'party-name-box--active' : ''}`}
          onClick={() => {
            setSelectedIdx(idx);
            handleSellerClick(r);
          }}
        >
          {r.companyName}
        </div>
      )
    }
  ];

  if (isSuperAdmin) {
    standardColumns.push({ key: 'buyerTradeName', label: 'COMPANY NAME', sortable: true });
  }

  standardColumns.push(
    { key: 'companyState', label: 'PLACE OF SUPPLY', sortable: true },
    { key: 'invoiceName', label: 'CHALLAN NAME' },
    { key: 'saleType', label: 'SALE TYPE' },
    { key: 'invoiceNo', label: 'CHALLAN NO', sortable: true },
    { key: 'invoiceDate', label: 'CHALLAN DATE', sortable: true, render: (r) => formatDateDDMMYYYY(r.invoiceDate) },
    { key: 'totalAmount', label: 'TOTAL AMOUNT', sortable: true }
  );

  if (hasPermission('Purchase Challan', 'view')) {
    standardColumns.push({
      key: 'print',
      label: 'PRINT',
      render: (r) => (
        <button type="button" className="list-page-icon-btn" title="Preview" onClick={() => onPreview && onPreview(r)}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
            <circle cx="12" cy="12" r="3"></circle>
          </svg>
        </button>
      )
    });
  }

  if (!isReport && (hasPermission('Purchase Challan', 'update') || hasPermission('Purchase Challan', 'delete'))) {
    standardColumns.push({
      key: 'actions',
      label: 'ACTIONS',
      render: (r) => (
        <div className="list-page-action-icons">
          {hasPermission('Purchase Challan', 'update') && (
            <button type="button" className="list-page-icon-btn" title="Edit" aria-label="Edit" onClick={() => startEdit(r)}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#7c3aed" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg>
            </button>
          )}
          {hasPermission('Purchase Invoice', 'add') && (
            <button type="button" className="list-page-icon-btn" title="Convert to Invoice" aria-label="Convert" onClick={() => onConvert && onConvert(r)}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"></path>
                <rect x="8" y="2" width="8" height="4" rx="1" ry="1"></rect>
                <path d="M9 14l2 2 4-4"></path>
              </svg>
            </button>
          )}
          {hasPermission('Purchase Challan', 'delete') && (
            <button
              type="button"
              className="list-page-icon-btn"
              title="Delete"
              aria-label="Delete"
              onClick={() => deleteRow(r._id)}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>
            </button>
          )}
        </div>
      )
    });
  }

  const columns = isReport ? reportColumns : standardColumns;

  const footerRow = pagedRows.length > 0 ? (
    isReport ? (
      <tr style={{ backgroundColor: '#eaeaea', fontWeight: 'bold' }}>
        <td colSpan={5} style={{ textAlign: 'right', fontWeight: 'bold', padding: '12px 10px', color: '#111827' }}>TOTAL</td>
        <td style={{ fontWeight: 'bold', padding: '12px 10px', color: '#111827' }}>
          {pagedRows.reduce((sum, r) => sum + (parseFloat(r.totalAmount) || 0), 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/-
        </td>
        <td></td>
        {hasPermission('Purchase Challan', 'view') && <td></td>}
      </tr>
    ) : (
      <tr style={{ backgroundColor: '#eaeaea', fontWeight: 'bold' }}>
        <td colSpan={isSuperAdmin ? 7 : 6} style={{ textAlign: 'right', fontWeight: 'bold', padding: '12px 10px', color: '#111827' }}>TOTAL</td>
        <td style={{ fontWeight: 'bold', padding: '12px 10px', color: '#111827' }}>
          {pagedRows.reduce((sum, r) => sum + (parseFloat(r.totalAmount) || 0), 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </td>
        {hasPermission('Purchase Challan', 'view') && <td></td>}
        {!isReport && (hasPermission('Purchase Challan', 'update') || hasPermission('Purchase Challan', 'delete')) && <td></td>}
      </tr>
    )
  ) : null;

  return (
    <div className="list-page-container">
      {/* Header: Title and Action Buttons */}
      <div className="list-page-header">
        <h1 className="list-page-title">{isReport ? 'Purchase Challan Report' : 'Purchase Challan List'}</h1>
        <div className="list-page-action-buttons">
          {hasPermission('Purchase Challan', 'report') && (
            <>
              <button className="list-page-btn list-page-btn-excel" onClick={() => {
                setExportType('excel');
                setIsExportModalOpen(true);
              }}>
                EXCEL
              </button>
              <button className="list-page-btn list-page-btn-print" onClick={() => {
                setExportType('print');
                setIsExportModalOpen(true);
              }}>
                PRINT
              </button>
            </>
          )}
          {!isReport && hasPermission('Purchase Challan', 'add') && (
            <button
              className="list-page-btn list-page-btn-add"
              onClick={() => onAddInvoice && onAddInvoice()}
            >
              ADD PURCHASE CHALLAN
            </button>
          )}
        </div>
      </div>

      <DataTable
        columns={columns}
        data={sortedRows}
        sortConfig={sortConfig}
        onSort={requestSort}
        loading={loading}
        emptyMessage="No records found"
        page={page}
        pageSize={pageSize}
        totalEntries={totalEntries}
        totalPages={totalPages}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        footerRow={footerRow}
        onRowClick={(r, idx) => {
          setSelectedIdx(idx);
        }}
        rowClassName={(r, idx) => idx === selectedIdx ? 'selected-row' : ''}
        searchBar={
          <>
            <SearchBar
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              placeholder="Search challans..."
            />
            <div className="list-page-filter-box">
              <div className="list-page-filter-group">
                <label className="list-page-filter-label">Start Date</label>
                <AirDatePicker
                  className="list-page-filter-date"
                  value={draftStartDate}
                  onChange={(val) => setDraftStartDate(val)}
                  placeholder="Start Date"
                />
              </div>

              <div className="list-page-filter-group">
                <label className="list-page-filter-label">End Date</label>
                <AirDatePicker
                  className="list-page-filter-date"
                  value={draftEndDate}
                  onChange={(val) => setDraftEndDate(val)}
                  placeholder="End Date"
                />
              </div>

              <div className="list-page-filter-group">
                <label className="list-page-filter-label">Seller</label>
                <select
                  className="list-page-filter-select"
                  value={draftSeller}
                  onChange={(e) => setDraftSeller(e.target.value)}
                >
                  <option value="">---Select Seller---</option>
                  {(Array.isArray(sellers) ? sellers : [])
                    .filter(s => s.sellerTradeName || s.sellerName)
                    .sort((a, b) => (a.sellerTradeName || a.sellerName).localeCompare(b.sellerTradeName || b.sellerName))
                    .map((s) => (
                      <option key={s.id || s._id} value={s.sellerTradeName || s.sellerName}>
                        {s.sellerTradeName}{s.sellerName ? ` (${s.sellerName})` : ''}
                      </option>
                    ))}
                </select>
              </div>

              <button className="list-page-btn list-page-btn-submit" onClick={onSubmitFilters}>
                Submit
              </button>
              <button className="list-page-btn list-page-btn-reset" onClick={onResetFilters}>
                Reset
              </button>
            </div>
          </>
        }
      />

      <AddPurchaseChallanForm isOpen={showAddForm} onClose={() => { closeForm(); fetchPurchaseInvoices(); }} editingRow={editingRow} />

      {isExportModalOpen && (
        <div style={{
          position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
          backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white', padding: '20px', borderRadius: '8px', width: '500px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)', maxHeight: '90vh', overflowY: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
              <h2 style={{ margin: 0, color: '#1e293b', fontSize: '18px', fontWeight: 'bold' }}>
                Select Columns to {exportType === 'excel' ? 'Export' : 'Print'}
              </h2>
              <button
                onClick={() => {
                  const allKeys = Object.keys(columnLabels);
                  const allChecked = allKeys.every(k => exportColumns[k]);
                  const newExportColumns = { ...exportColumns };
                  allKeys.forEach(k => newExportColumns[k] = !allChecked);
                  setExportColumns(newExportColumns);
                }}
                style={{
                  padding: '4px 8px', fontSize: '12px', borderRadius: '4px', border: '1px solid #cbd5e1',
                  backgroundColor: '#f8fafc', cursor: 'pointer'
                }}
              >
                {Object.keys(columnLabels).every(k => exportColumns[k]) ? 'Deselect All' : 'Select All'}
              </button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '20px' }}>
              {Object.entries(columnLabels).map(([key, label]) => {
                if (key === 'buyerTradeName' && !isSuperAdmin) return null;
                return (
                  <label key={key} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', color: '#333', cursor: 'pointer', padding: '4px' }}>
                    <input
                      type="checkbox"
                      checked={exportColumns[key]}
                      onChange={() => toggleExportColumn(key)}
                      style={{ accentColor: '#06b6d4', width: '16px', height: '16px' }}
                    />
                    {label}
                  </label>
                );
              })}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', pt: '10px', borderTop: '1px solid #e2e8f0', marginTop: '10px', paddingTop: '15px' }}>
              <button
                onClick={() => setIsExportModalOpen(false)}
                style={{
                  padding: '8px 16px', borderRadius: '4px', border: '1px solid #cbd5e1',
                  backgroundColor: 'white', color: '#475569', cursor: 'pointer', fontWeight: '600'
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (exportType === 'excel') {
                    handleExcelDownload();
                  } else {
                    handlePrint();
                  }
                  setIsExportModalOpen(false);
                }}
                style={{
                  padding: '8px 16px', borderRadius: '4px', border: 'none',
                  backgroundColor: '#06b6d4', color: 'white', cursor: 'pointer', fontWeight: '600'
                }}
              >
                {exportType === 'excel' ? 'Export Excel' : 'Print'}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* COMPREHENSIVE HISTORY SECTION - Shows only Converted challans */}
      <div style={{ marginTop: '50px', borderTop: '3px solid #1e3a5f', paddingTop: '30px', paddingBottom: '50px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={{ fontSize: '20px', color: '#1e3a5f', fontWeight: 'bold', margin: 0 }}>COMPREHENSIVE HISTORY</h2>
          <span style={{ fontSize: '13px', color: '#64748b' }}>Showing last 20 converted challans</span>
        </div>
        <div style={{ boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)', borderRadius: '8px', overflow: 'hidden' }}>
          <DataTable
            columns={[
              { key: 'challan_no', label: 'CHALLAN NO', render: h => h.challan_no || h.invoiceNo },
              { key: 'date', label: 'DATE', render: h => formatDateDDMMYYYY(h.challan_date || h.invoiceDate) },
              { key: 'seller_name', label: 'SELLER NAME', render: h => h.company_name || h.companyName },
              { key: 'sale_type', label: 'SALE TYPE', render: h => h.saleType || h.sale_type_name },
              {
                key: 'total_amount',
                label: 'TOTAL AMOUNT',
                style: { textAlign: 'right', fontWeight: '600', color: '#059669' },
                render: h => `₹${parseFloat(h.total_amount || h.totalAmount || 0).toFixed(2)}`
              }
            ]}
            data={history}
            loading={false}
            emptyMessage="No history found"
            page={1}
            pageSize={history.length > 0 ? history.length : 10}
            totalEntries={history.length}
            totalPages={1}
            onPageChange={() => {}}
            onPageSizeChange={() => {}}
            footerRow={
              history.length > 0 ? (
                <tr style={{ backgroundColor: '#eaeaea', fontWeight: 'bold' }}>
                  <td colSpan="4" style={{ textAlign: 'right', fontWeight: 'bold', padding: '12px 10px', color: '#111827' }}>TOTAL</td>
                  <td style={{ textAlign: 'right', fontWeight: 'bold', padding: '12px 10px', color: '#111827' }}>
                    ₹{history.reduce((sum, h) => sum + (parseFloat(h.total_amount || h.totalAmount || 0)), 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </td>
                </tr>
              ) : null
            }
          />
        </div>
      </div>
    </div>
  );
}

export default PurchaseChallanList;
